import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { KNIT_PROGRAM } from "@/content";
import { datCell, datPixels, readDat, type DatCell, type DatLabels, type DatPicture } from "./dat";
import { ProgramTextPanel } from "./ProgramTextPanel";
import { fetchGzipped } from "./programText";

const MAX_PIXEL_RATIO = 2;
// The most a cell is drawn at, in CSS pixels; the least is the picture fitting the view's width.
const MAX_CELL = 24;
const ZOOM_STEP = 1.6;
const VIEWPORT = "#be4720";

type Loaded = {
  readonly picture: DatPicture;
  readonly labels: DatLabels;
  // the picture at one canvas pixel per cell, top row first
  readonly image: HTMLCanvasElement;
};

type Status = "idle" | "loading" | "ready" | "failed";

// Where the view is: the size of a cell in CSS pixels, and the picture's column and row (counted
// from the top) at the view's top left corner.
type View = { cell: number; left: number; top: number };

const formatCount = (value: number) => value.toLocaleString("en-US");

const loadProgram = async (): Promise<Loaded> => {
  const [buffer, labels] = await Promise.all([
    fetchGzipped(KNIT_PROGRAM.packed),
    fetch(KNIT_PROGRAM.labels).then((response) => {
      if (!response.ok) throw new Error(`${KNIT_PROGRAM.labels}: ${response.status}`);
      return response.json() as Promise<DatLabels>;
    }),
  ]);
  const picture = readDat(buffer);
  const image = document.createElement("canvas");
  image.width = picture.width;
  image.height = picture.height;
  image.getContext("2d")?.putImageData(new ImageData(datPixels(picture), picture.width, picture.height), 0, 0);
  return { picture, labels, image };
};

// Suzanne's machine program as the picture a knitting program is: one row per carriage pass, the
// needles across the middle and the option lines down the sides, to move round, zoom into and read
// cell by cell, beside a strip of the whole program, the same program as text and its downloads.
export const DatViewer = () => {
  const [status, setStatus] = useState<Status>("idle");
  const [hovered, setHovered] = useState<DatCell | null>(null);
  // the DAT row in the middle of the view, counted from 0 at the bottom
  const [centreRow, setCentreRow] = useState(0);

  const figureRef = useRef<HTMLElement>(null);
  const viewRef = useRef<HTMLCanvasElement>(null);
  const overviewRef = useRef<HTMLCanvasElement>(null);
  const loadedRef = useRef<Loaded | null>(null);
  const stateRef = useRef<View | null>(null);
  const dragRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  // a two-finger touch: where it started, and the view then
  const pinchRef = useRef<{ distance: number; x: number; y: number; view: View } | null>(null);
  // where a finger went down on the overview strip, to tell a tap from a swipe
  const overviewTapRef = useRef<{ x: number; y: number } | null>(null);
  // On a touch screen one finger scrolls the page, as everywhere else on it; two move the program.
  const [touch, setTouch] = useState(false);

  useEffect(() => {
    const coarse = window.matchMedia("(pointer: coarse)");
    const update = () => setTouch(coarse.matches);
    update();
    coarse.addEventListener("change", update);
    return () => coarse.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const figure = figureRef.current;
    if (!figure) return;
    const nearby = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatus((current) => (current === "idle" ? "loading" : current));
      },
      { rootMargin: "400px 0px" },
    );
    nearby.observe(figure);
    return () => nearby.disconnect();
  }, []);

  useEffect(() => {
    if (status !== "loading") return;
    let cancelled = false;
    loadProgram()
      .then((loaded) => {
        if (cancelled) return;
        loadedRef.current = loaded;
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  // A cell no bigger than MAX_CELL, nor smaller than the picture's width fitting the view.
  const clampCell = useCallback((cell: number) => {
    const canvas = viewRef.current;
    const loaded = loadedRef.current;
    if (!canvas || !loaded) return cell;
    return Math.min(Math.max(cell, canvas.clientWidth / loaded.picture.width), MAX_CELL);
  }, []);

  // The view kept inside the picture, no smaller than its width.
  const settle = useCallback(
    (next: View) => {
      const canvas = viewRef.current;
      const loaded = loadedRef.current;
      if (!canvas || !loaded) return next;
      const { width, height } = loaded.picture;
      const cell = clampCell(next.cell);
      const columns = canvas.clientWidth / cell;
      const rows = canvas.clientHeight / cell;
      return {
        cell,
        left: Math.min(Math.max(next.left, 0), Math.max(width - columns, 0)),
        top: Math.min(Math.max(next.top, 0), Math.max(height - rows, 0)),
      };
    },
    [clampCell],
  );

  const draw = useCallback(() => {
    const canvas = viewRef.current;
    const overview = overviewRef.current;
    const loaded = loadedRef.current;
    const view = stateRef.current;
    if (!canvas || !overview || !loaded || !view) return;
    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    for (const target of [canvas, overview]) {
      const width = Math.max(1, Math.round(target.clientWidth * ratio));
      const height = Math.max(1, Math.round(target.clientHeight * ratio));
      if (target.width !== width || target.height !== height) {
        target.width = width;
        target.height = height;
      }
    }
    const { image, picture } = loaded;
    const columns = canvas.clientWidth / view.cell;
    const rows = canvas.clientHeight / view.cell;
    const context = canvas.getContext("2d");
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = false;
      context.drawImage(image, view.left, view.top, columns, rows, 0, 0, canvas.width, canvas.height);
    }
    const strip = overview.getContext("2d");
    if (strip) {
      strip.clearRect(0, 0, overview.width, overview.height);
      strip.imageSmoothingEnabled = true;
      strip.drawImage(image, 0, 0, overview.width, overview.height);
      const scale = overview.height / picture.height;
      strip.strokeStyle = VIEWPORT;
      strip.lineWidth = 2 * ratio;
      const top = view.top * scale;
      const height = Math.max(rows * scale, 3 * ratio);
      strip.strokeRect(ratio, top + ratio, overview.width - 2 * ratio, Math.max(height - 2 * ratio, ratio));
    }
  }, []);

  const moveTo = useCallback(
    (next: View) => {
      const view = settle(next);
      stateRef.current = view;
      draw();
      const canvas = viewRef.current;
      const loaded = loadedRef.current;
      if (canvas && loaded) {
        const middle = Math.floor(view.top + canvas.clientHeight / view.cell / 2);
        setCentreRow(Math.max(loaded.picture.height - 1 - middle, 0));
      }
    },
    [draw, settle],
  );

  // The view centred on a DAT row, for a course picked in the text.
  const showRow = useCallback(
    (row: number) => {
      const view = stateRef.current;
      const canvas = viewRef.current;
      const loaded = loadedRef.current;
      if (!view || !canvas || !loaded) return;
      const fromTop = loaded.picture.height - 1 - row;
      moveTo({ ...view, top: fromTop + 0.5 - canvas.clientHeight / view.cell / 2 });
    },
    [moveTo],
  );

  // Once loaded: the whole width, at the bottom, where the machine starts.
  useEffect(() => {
    if (status !== "ready") return;
    const canvas = viewRef.current;
    const loaded = loadedRef.current;
    if (!canvas || !loaded) return;
    const cell = canvas.clientWidth / loaded.picture.width;
    moveTo({ cell, left: 0, top: loaded.picture.height });
    const observer = new ResizeObserver(() => {
      if (stateRef.current) moveTo(stateRef.current);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [status, moveTo]);

  const zoomAt = useCallback(
    (factor: number, x: number, y: number) => {
      const view = stateRef.current;
      if (!view) return;
      const column = view.left + x / view.cell;
      const row = view.top + y / view.cell;
      const cell = view.cell * factor;
      moveTo({ cell, left: column - x / cell, top: row - y / cell });
    },
    [moveTo],
  );

  // A pinch, or the wheel with Ctrl or ⌘, zooms; the plain wheel is left to the page. React's wheel
  // listener is passive, so it could not keep the browser from zooming the page instead.
  useEffect(() => {
    const canvas = viewRef.current;
    if (status !== "ready" || !canvas) return;
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const bounds = canvas.getBoundingClientRect();
      zoomAt(Math.exp(-event.deltaY * 0.01), event.clientX - bounds.left, event.clientY - bounds.top);
    };
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [status, zoomAt]);

  // Two fingers on the program: the cell under their middle stays under it while they pinch and move.
  // The listeners are native so a two-finger move can keep the page from scrolling.
  useEffect(() => {
    const canvas = viewRef.current;
    if (status !== "ready" || !canvas) return;
    const fingers = (event: TouchEvent) => {
      const bounds = canvas.getBoundingClientRect();
      const [a, b] = [event.touches[0], event.touches[1]];
      return {
        distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        x: (a.clientX + b.clientX) / 2 - bounds.left,
        y: (a.clientY + b.clientY) / 2 - bounds.top,
      };
    };
    const handleStart = (event: TouchEvent) => {
      const view = stateRef.current;
      if (event.touches.length !== 2 || !view) return;
      dragRef.current = null;
      pinchRef.current = { ...fingers(event), view };
    };
    const handleMove = (event: TouchEvent) => {
      const pinch = pinchRef.current;
      if (event.touches.length !== 2 || !pinch) return;
      event.preventDefault();
      const now = fingers(event);
      const column = pinch.view.left + pinch.x / pinch.view.cell;
      const row = pinch.view.top + pinch.y / pinch.view.cell;
      const cell = clampCell(pinch.view.cell * (now.distance / Math.max(pinch.distance, 1)));
      moveTo({ cell, left: column - now.x / cell, top: row - now.y / cell });
    };
    const handleEnd = (event: TouchEvent) => {
      if (event.touches.length < 2) pinchRef.current = null;
    };
    canvas.addEventListener("touchstart", handleStart, { passive: true });
    canvas.addEventListener("touchmove", handleMove, { passive: false });
    canvas.addEventListener("touchend", handleEnd);
    canvas.addEventListener("touchcancel", handleEnd);
    return () => {
      canvas.removeEventListener("touchstart", handleStart);
      canvas.removeEventListener("touchmove", handleMove);
      canvas.removeEventListener("touchend", handleEnd);
      canvas.removeEventListener("touchcancel", handleEnd);
    };
  }, [status, clampCell, moveTo]);

  const zoomCentre = (factor: number) => {
    const canvas = viewRef.current;
    if (canvas) zoomAt(factor, canvas.clientWidth / 2, canvas.clientHeight / 2);
  };

  const fitWidth = () => {
    const view = stateRef.current;
    const canvas = viewRef.current;
    const loaded = loadedRef.current;
    if (!view || !canvas || !loaded) return;
    const middle = view.top + canvas.clientHeight / view.cell / 2;
    const cell = canvas.clientWidth / loaded.picture.width;
    moveTo({ cell, left: 0, top: middle - canvas.clientHeight / cell / 2 });
  };

  const cellAt = (event: PointerEvent<HTMLCanvasElement>) => {
    const view = stateRef.current;
    const loaded = loadedRef.current;
    if (!view || !loaded) return null;
    const bounds = event.currentTarget.getBoundingClientRect();
    const column = Math.floor(view.left + (event.clientX - bounds.left) / view.cell);
    const fromTop = Math.floor(view.top + (event.clientY - bounds.top) / view.cell);
    return datCell(loaded.picture, loaded.labels, column, loaded.picture.height - 1 - fromTop);
  };

  // A finger only taps here (two move the view); a mouse or pen drags.
  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    const view = stateRef.current;
    if (!view) return;
    if (event.pointerType !== "touch") event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, left: view.left, top: view.top };
  };

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    const view = stateRef.current;
    if (drag && view && event.pointerType !== "touch" && event.currentTarget.hasPointerCapture(event.pointerId)) {
      moveTo({
        ...view,
        left: drag.left - (event.clientX - drag.x) / view.cell,
        top: drag.top - (event.clientY - drag.y) / view.cell,
      });
      return;
    }
    if (event.pointerType === "mouse") setHovered(cellAt(event));
  };

  const handlePointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
    if (dragRef.current && Math.hypot(event.clientX - dragRef.current.x, event.clientY - dragRef.current.y) < 4) {
      // a tap reads the cell, where there is no hover
      setHovered(cellAt(event));
    }
    dragRef.current = null;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLCanvasElement>) => {
    const view = stateRef.current;
    const canvas = viewRef.current;
    if (!view || !canvas) return;
    const page = canvas.clientHeight / view.cell;
    const moves: Record<string, Partial<View>> = {
      ArrowUp: { top: view.top - page / 8 },
      ArrowDown: { top: view.top + page / 8 },
      ArrowLeft: { left: view.left - canvas.clientWidth / view.cell / 8 },
      ArrowRight: { left: view.left + canvas.clientWidth / view.cell / 8 },
      PageUp: { top: view.top - page },
      PageDown: { top: view.top + page },
      Home: { top: 0 },
      End: { top: Infinity },
    };
    if (event.key === "+" || event.key === "=") zoomCentre(ZOOM_STEP);
    else if (event.key === "-") zoomCentre(1 / ZOOM_STEP);
    else if (event.key in moves) moveTo({ ...view, ...moves[event.key] });
    else return;
    event.preventDefault();
  };

  const seekOverview = (event: PointerEvent<HTMLCanvasElement>) => {
    const view = stateRef.current;
    const canvas = viewRef.current;
    const loaded = loadedRef.current;
    if (!view || !canvas || !loaded) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const row = ((event.clientY - bounds.top) / bounds.height) * loaded.picture.height;
    moveTo({ ...view, top: row - canvas.clientHeight / view.cell / 2 });
  };

  // A mouse or pen drags along the strip; a finger taps it, so a swipe there still scrolls the page.
  const handleOverviewDown = (event: PointerEvent<HTMLCanvasElement>) => {
    if (event.pointerType === "touch") {
      overviewTapRef.current = { x: event.clientX, y: event.clientY };
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    seekOverview(event);
  };

  const handleOverviewMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (event.pointerType !== "touch" && event.currentTarget.hasPointerCapture(event.pointerId)) seekOverview(event);
  };

  const handleOverviewUp = (event: PointerEvent<HTMLCanvasElement>) => {
    const tap = overviewTapRef.current;
    overviewTapRef.current = null;
    if (tap && Math.hypot(event.clientX - tap.x, event.clientY - tap.y) < 8) seekOverview(event);
  };

  const picture = loadedRef.current?.picture;
  const buttons = [
    { label: KNIT_PROGRAM.zoomOut, symbol: "−", onClick: () => zoomCentre(1 / ZOOM_STEP) },
    { label: KNIT_PROGRAM.zoomIn, symbol: "+", onClick: () => zoomCentre(ZOOM_STEP) },
    { label: KNIT_PROGRAM.fit, symbol: "↔", onClick: fitWidth },
  ];
  const downloads = [KNIT_PROGRAM.file, KNIT_PROGRAM.text.knitout, KNIT_PROGRAM.text.knitcode];

  return (
    <Box
      ref={figureRef}
      as="figure"
      className="print-avoid-break"
      borderRadius="lg"
      overflow="hidden"
      bg="surface.white"
      border="1px solid"
      borderColor="line.hairline"
    >
      <Grid
        templateColumns={{
          base: "minmax(0, 1fr) clamp(44px, 12vw, 64px)",
          lg: "minmax(0, 1fr) clamp(44px, 5vw, 64px) minmax(0, 1fr)",
        }}
        templateAreas={{ base: `"dat overview" "text text"`, lg: `"dat overview text"` }}
      >
        <Box gridArea="dat" position="relative" minW={0} h="clamp(420px, 62vh, 640px)" bg="#000">
          <canvas
            ref={viewRef}
            role="img"
            tabIndex={0}
            aria-label={`${KNIT_PROGRAM.label}. ${KNIT_PROGRAM.description}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => {
              dragRef.current = null;
            }}
            onPointerLeave={(event) => {
              // a lifted finger leaves too, and its tap's reading stays
              if (event.pointerType === "mouse") setHovered(null);
            }}
            onKeyDown={handleKeyDown}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              touchAction: "pan-y",
              cursor: "grab",
              imageRendering: "pixelated",
            }}
          />
          {status === "failed" && (
            <Text
              position="absolute"
              left="18px"
              right="18px"
              bottom="14px"
              variant="small"
              color="#fff"
              pointerEvents="none"
            >
              {KNIT_PROGRAM.failed}
            </Text>
          )}
        </Box>
        <Grid
          gridArea="overview"
          templateRows="auto minmax(0, 1fr)"
          borderLeft="1px solid"
          borderColor="line.hairline"
          bg="surface.DEFAULT"
        >
          <Text variant="eyebrow" textAlign="center" pt="16px" pb="10px" fontSize="0.625rem" letterSpacing="0.1em">
            {KNIT_PROGRAM.overviewLabel}
          </Text>
          <Box position="relative" mx="clamp(6px, 1vw, 10px)" mb="12px">
            <canvas
              ref={overviewRef}
              aria-hidden="true"
              onPointerDown={handleOverviewDown}
              onPointerMove={handleOverviewMove}
              onPointerUp={handleOverviewUp}
              onPointerCancel={() => {
                overviewTapRef.current = null;
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                touchAction: "pan-y",
                cursor: "ns-resize",
              }}
            />
          </Box>
        </Grid>
        <Box
          gridArea="text"
          minW={0}
          h={{ base: "340px", lg: "clamp(420px, 62vh, 640px)" }}
          borderLeft={{ lg: "1px solid" }}
          borderTop={{ base: "1px solid", lg: "none" }}
          borderColor="line.hairline"
        >
          <ProgramTextPanel row={hovered?.row ?? centreRow} onSelectRow={showRow} load={status !== "idle"} />
        </Box>
      </Grid>

      <Flex align="center" gap="16px" px="18px" py="12px" borderTop="1px solid" borderColor="line.hairline" minH="64px">
        <Box flex="1" minW={0} aria-live="polite">
          {hovered ? (
            <Text variant="small" lineHeight={1.4}>
              <Box as="span" color="ink.DEFAULT">
                {KNIT_PROGRAM.rowLabel} {formatCount(hovered.row)} · {hovered.place}
              </Box>
              <br />
              {hovered.meaning} (colour {hovered.code})
            </Text>
          ) : (
            <Text variant="small" lineHeight={1.4}>
              {picture ? `${formatCount(picture.height)} rows, ${formatCount(picture.width)} columns. ` : ""}
              {touch ? KNIT_PROGRAM.touchHint : KNIT_PROGRAM.hint}
            </Text>
          )}
        </Box>
        <Flex gap="8px" flexShrink={0}>
          {buttons.map((button) => (
            <Box
              key={button.label}
              as="button"
              type="button"
              aria-label={button.label}
              title={button.label}
              onClick={button.onClick}
              w="40px"
              h="40px"
              display="grid"
              placeItems="center"
              borderRadius="full"
              bg="surface.DEFAULT"
              color="ink.DEFAULT"
              border="1px solid"
              borderColor="line.hairline"
              fontSize="1.1rem"
              lineHeight={1}
              transitionProperty="background-color"
              transitionDuration="0.25s"
              transitionTimingFunction="brand"
              _hover={{ bg: "brand.600", color: "#fff", borderColor: "brand.600" }}
            >
              <span aria-hidden="true">{button.symbol}</span>
            </Box>
          ))}
        </Flex>
      </Flex>

      <Flex
        wrap="wrap"
        align="center"
        gap="10px 12px"
        px="18px"
        py="16px"
        borderTop="1px solid"
        borderColor="line.hairline"
      >
        <Text variant="eyebrow" fontSize="0.625rem" letterSpacing="0.1em" mr="6px">
          {KNIT_PROGRAM.downloadsLabel}
        </Text>
        {downloads.map((file, index) => (
          <Box
            key={file.src}
            as="a"
            href={file.src}
            download={file.name}
            display="inline-flex"
            alignItems="center"
            gap="10px"
            px="18px"
            py="10px"
            borderRadius="full"
            bg={index ? "surface.DEFAULT" : "ink.DEFAULT"}
            color={index ? "ink.DEFAULT" : "#fff"}
            border="1px solid"
            borderColor={index ? "line.hairline" : "ink.DEFAULT"}
            fontSize="small"
            whiteSpace="nowrap"
            transitionProperty="background-color, color, border-color"
            transitionDuration="0.25s"
            transitionTimingFunction="brand"
            _hover={{ bg: "brand.600", color: "#fff", borderColor: "brand.600" }}
          >
            {file.label}
            <Box as="span" opacity={0.7}>
              {file.size}
            </Box>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};
